/**
 * Render-time sanitizer for warehouse-sourced HTML visit-note bodies (D1, ADR-065).
 *
 * `FROZENENCOUNTERHTML` (adopted as the visit-note body, ADR-065) is a finalized
 * EHR encounter rendering — a wall of `onclick`/inline-`style`-heavy HTML the EHR
 * vendor authored for its own UI, not for us. It is warehouse clob content and is
 * therefore UNTRUSTED: this module is the ONLY place that content is allowed to
 * reach `dangerouslySetInnerHTML`, and only after passing through DOMPurify with a
 * strict, explicit allowlist. Every event handler (`on*`), every `<script>`, every
 * `style`/`class` attribute, and every hyperlink/image/form element is stripped —
 * this is a clinical-content security surface, not a formatting nicety.
 */
import DOMPurify from "dompurify";

/** Block/structural + inline-formatting tags a finalized encounter body may use. */
const ALLOWED_TAGS = [
  "p",
  "br",
  "div",
  "span",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "ul",
  "ol",
  "li",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
  "hr",
  "sub",
  "sup",
];

/**
 * The only attributes ever permitted through the allowlist — table-layout
 * spans only. Deliberately excludes `style`, `class`, `id`, every `on*` event
 * handler, `href`/`src` (no links or images from untrusted clob content), and
 * every `data-*` attribute (a CSS/attribute-selector exfiltration vector).
 */
const ALLOWED_ATTR = ["colspan", "rowspan"];

/**
 * Detect whether a source body looks like HTML markup rather than plain text.
 *
 * The visit-note body is either the frozen `FROZENENCOUNTERHTML` rendering (an
 * HTML document/fragment) or the legacy `[KEY]`-labelled plain-text fallback —
 * this discriminates which render path a source body needs. A false negative
 * (HTML mis-detected as plain text) only costs a less-formatted display, never
 * a security gap: {@link sanitizeSourceHtml} is the only path that touches
 * `dangerouslySetInnerHTML`, so plain text is never at risk of executing.
 *
 * Checks for standard HTML document root tags and fragments:
 * - Doctype: `<!DOCTYPE\s+html`
 * - Standard tags: `<html>`, `<body>`, `<head>`, `<div>`, `<p>`, `<table>`,
 *   `<section>`, `<article>`, `<main>`, `<header>`, `<footer>`, `<h1-h6>`.
 *
 * Safety bounds:
 * - Plain text with stray tags is not treated as HTML document/fragment.
 * - Markdown headings (`# Psychotherapy`) remain plain text.
 * - Clinical bracketed placeholders like `<symptoms>` or `<patient>` remain plain text.
 *
 * @param body - The raw, unsanitized source document body.
 * @returns `true` if `body` starts with standard HTML document/fragment tags.
 */
export function looksLikeHtml(body: string): boolean {
  const trimmed = body.trim();
  if (trimmed === "" || trimmed.startsWith("#")) {
    return false;
  }
  return /^(?:<!DOCTYPE\s+html|<(?:html|body|head|div|p|table|section|article|main|header|footer|h[1-6])\b)/i.test(
    trimmed,
  );
}

/**
 * Remove spacer tables that only contain empty cells, whitespace, and non-breaking spaces.
 *
 * @param root - The DOM container element or document fragment to clean in-place.
 */
function removeSpacerTables(root: Element): void {
  const tables = root.querySelectorAll("table");
  for (let i = tables.length - 1; i >= 0; i -= 1) {
    const table = tables[i];
    // If the text content contains only whitespace or &nbsp; (\u00A0)
    const textContent = table.textContent?.replace(/[\s\u00A0]+/g, "") ?? "";
    if (textContent === "") {
      table.remove();
    }
  }
}

/**
 * Sanitize untrusted warehouse HTML down to a strict, explicit allowlist and
 * clean up messy Athena EHR artifacts (spacer tables, &nbsp; runs, vendor buttons).
 *
 * Strips every `<script>`, every `on*` event handler, every inline `style`,
 * every `class`/`id`, every link/image/form/iframe/object/embed element, and
 * any other tag not in {@link ALLOWED_TAGS}.
 *
 * Specific cleanups:
 * 1. Vendor buttons: Completely strip `<button>` and `<input>` elements including
 *    their text/content (e.g. `<button>Print</button>`, `<input type="button" ...>`).
 * 2. Spacer tables: Remove tables that only contain whitespace, empty cells, and `&nbsp;`.
 * 3. `&nbsp;` runs: Collapse consecutive `&nbsp;` entities or non-breaking spaces
 *    down to a single space or `&nbsp;`.
 *
 * @param html - The raw, unsanitized source document body.
 * @returns Sanitized HTML markup safe to pass to `dangerouslySetInnerHTML`.
 */
export function sanitizeSourceHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ["button", "input"],
    ADD_FORBID_CONTENTS: ["button", "input"],
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
    RETURN_DOM: true,
  });

  if (clean instanceof Element) {
    removeSpacerTables(clean);
  }

  const serialized = clean instanceof Element ? clean.innerHTML : String(clean);

  return serialized.replace(
    /(?:&nbsp;|\u00A0)(?:(?:\s|&nbsp;|\u00A0)*(?:&nbsp;|\u00A0))+/gi,
    " ",
  );
}
